import { AuditedProduct } from './scoring-engine-constants';
import {
  PACVUE_DEFAULT_MARKET,
  PACVUE_HEADERS,
  PACVUE_INSTRUCTIONS_ROW,
  PACVUE_MAX_ROWS_PER_FILE,
  PacvueColumn,
  PacvueExportConfig,
  PacvueExportFile,
  PacvueExportPlan,
  PacvueExportResult,
  PacvuePlannedFile,
  PacvueSkipReason,
  PacvueSkippedRow
} from './pacvue-export-constants';
import { ITEM_HIGHLIGHT_CHAR_LIMIT, TITLE_CHAR_LIMIT } from './title-compliance-constants';
import { TitleRewriteStore, isMissingTitle } from './title-compliance-utils';

// Builds the Pacvue "Update Listing For Vendor" bulk CSV from saved AI title /
// item-highlight rewrites. See pacvue-export-constants.ts for the column
// contract this file writes into.
//
// The output of this module edits LIVE Amazon listings once an account manager
// uploads it, so the bias throughout is: emit nothing we did not generate, and
// refuse (loudly) rather than guess. Every product left out is reported in
// `skipped` with a reason — there is no silent drop anywhere in this file.

/**
 * A leading =, +, -, @ or tab is interpreted as a formula by Excel/Sheets.
 * PDP_TITLE, ITEM_NAME and ITEM_HIGHLIGHTS are seeded from scraped Amazon
 * listing titles — third-party, unvalidated input — so any cell starting
 * with one of these is neutralized before an operator can open the file in
 * a spreadsheet app on the way to a Pacvue upload.
 */
const CSV_FORMULA_TRIGGER = /^[=+\-@\t]/;

/**
 * RFC 4180 quoting, plus formula-injection neutralization. The instruction
 * block alone carries commas, quotes-free text and embedded newlines, and
 * generated titles routinely contain commas, so every cell goes through
 * this — never string-concatenated raw.
 */
const escapeCsvCell = (value: string): string => {
  const safe = CSV_FORMULA_TRIGGER.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** RFC 4180 row separator. Embedded newlines inside a quoted cell are left alone. */
const ROW_SEPARATOR = '\r\n';

/**
 * The scraper's "no value" sentinel check. `isMissingTitle` is named for titles
 * but implements the parser-wide sentinel rule (`''`, `'—'`, `' — '`), so it is
 * reused here for ASINs rather than defining a second copy that could drift.
 */
const isMissingValue = isMissingTitle;

/** A blank row of the right width — every column empty until we fill one. */
const emptyRow = (): string[] => new Array<string>(PACVUE_HEADERS.length).fill('');

/**
 * Config problems that make the whole export unusable. Account ID and Vendor
 * Code are red/required columns in the template, and leaving them blank is the
 * documented cause of Candice's earlier upload failures — so we block the
 * download rather than hand someone a file that will be rejected.
 */
export const validatePacvueConfig = (config: PacvueExportConfig): string[] => {
  const errors: string[] = [];
  if (!config.accountId.trim()) {
    errors.push('Account ID is required — get it from Pacvue for this account.');
  }
  if (!config.vendorCode.trim()) {
    errors.push('Vendor Code is required.');
  }
  const max = config.maxRowsPerFile ?? PACVUE_MAX_ROWS_PER_FILE;
  if (!Number.isInteger(max) || max < 1 || max > PACVUE_MAX_ROWS_PER_FILE) {
    errors.push(`Rows per file must be a whole number between 1 and ${PACVUE_MAX_ROWS_PER_FILE}.`);
  }
  return errors;
};

// ── Remembered account identifiers ──
//
// Account ID and Vendor Code are per-account constants an operator would
// otherwise retype on every export. They are identifiers, not credentials —
// the same values get typed into a spreadsheet and emailed around — so
// localStorage is an appropriate home, consistent with how this feature already
// persists rewrites. Market is stored alongside them for the same reason.

const PACVUE_CONFIG_STORAGE_KEY = 'pdp-scorecard-pacvue-config-v1';

/** The remembered subset of `PacvueExportConfig` — never the per-run overrides. */
export interface StoredPacvueConfig {
  accountId: string;
  vendorCode: string;
  market: string;
}

export const EMPTY_PACVUE_CONFIG: StoredPacvueConfig = {
  accountId: '',
  vendorCode: '',
  market: PACVUE_DEFAULT_MARKET
};

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');

/** Reads the remembered identifiers, falling back to empties on anything malformed. */
export const readPacvueConfig = (): StoredPacvueConfig => {
  try {
    const raw = localStorage.getItem(PACVUE_CONFIG_STORAGE_KEY);
    if (!raw) return { ...EMPTY_PACVUE_CONFIG };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...EMPTY_PACVUE_CONFIG };
    const record = parsed as Record<string, unknown>;
    return {
      accountId: asString(record.accountId),
      vendorCode: asString(record.vendorCode),
      market: asString(record.market) || PACVUE_DEFAULT_MARKET
    };
  } catch {
    return { ...EMPTY_PACVUE_CONFIG };
  }
};

export const writePacvueConfig = (config: StoredPacvueConfig): void => {
  try {
    localStorage.setItem(PACVUE_CONFIG_STORAGE_KEY, JSON.stringify(config));
  } catch {
    // Quota / disabled — non-fatal, the operator just retypes next time.
  }
};

interface SelectionResult {
  rows: string[][];
  skipped: PacvueSkippedRow[];
}

/**
 * Turn scored products plus their saved rewrites into Pacvue data rows.
 *
 * Products are visited in the order given so the export matches what the user
 * sees in the compliance table. Exactly one row is produced per exported
 * product; everything else lands in `skipped`.
 */
export const selectPacvueRows = (
  products: AuditedProduct[],
  store: TitleRewriteStore,
  config: PacvueExportConfig
): SelectionResult => {
  const market = (config.market ?? PACVUE_DEFAULT_MARKET).trim() || PACVUE_DEFAULT_MARKET;
  const accountId = config.accountId.trim();
  const vendorCode = config.vendorCode.trim();

  const rows: string[][] = [];
  const skipped: PacvueSkippedRow[] = [];
  // Template instruction 2: "There cannot be duplicate ASIN-Market data."
  const seen = new Set<string>();

  for (const product of products) {
    const asin = (product.asin ?? '').trim();
    const skip = (reason: PacvueSkippedRow['reason']): void => {
      skipped.push({ productId: product.id, asin, reason });
    };

    const rewrite = store[product.id];
    if (!rewrite) {
      skip(PacvueSkipReason.NO_REWRITE);
      continue;
    }

    // A record can legitimately hold a highlight with no title rewrite (an
    // already-compliant title the user still wanted a highlight for), so these
    // are checked independently rather than gating one on the other.
    const title = (rewrite.title ?? '').trim();
    const highlight = (rewrite.highlight?.value ?? '').trim();
    if (!title && !highlight) {
      skip(PacvueSkipReason.NOTHING_TO_UPDATE);
      continue;
    }

    // Belt-and-braces against a corrupted or hand-edited localStorage record:
    // the generator hard-validates both caps, so these should be unreachable,
    // but an over-cap value here would be written straight onto a live listing.
    if (title.length > TITLE_CHAR_LIMIT) {
      skip(PacvueSkipReason.TITLE_OVER_CAP);
      continue;
    }
    if (highlight.length > ITEM_HIGHLIGHT_CHAR_LIMIT) {
      skip(PacvueSkipReason.HIGHLIGHT_OVER_CAP);
      continue;
    }

    if (isMissingValue(asin)) {
      skip(PacvueSkipReason.MISSING_ASIN);
      continue;
    }

    // JSON-encoded pair rather than a delimiter-joined string, so no ASIN or
    // market value can be crafted to collide with a different pair.
    const key = JSON.stringify([asin, market]);
    if (seen.has(key)) {
      skip(PacvueSkipReason.DUPLICATE_ASIN);
      continue;
    }
    seen.add(key);

    const row = emptyRow();
    // Column A is grey/reference — the current title, so a reviewer can read
    // the before and after side by side in the sheet. Not a field Pacvue writes.
    row[PacvueColumn.PDP_TITLE] = isMissingValue(product.title) ? '' : product.title;
    row[PacvueColumn.ASIN] = asin;
    // P-ASIN stays blank: this exporter is child-ASIN only (instruction 6 —
    // fill one or the other). Parent support is blocked on Pacvue clarifying
    // whether UPC/EAN, ISBN or Model Number is genuinely required for parents.
    row[PacvueColumn.MARKET] = market;
    row[PacvueColumn.ACCOUNT_ID] = accountId;
    row[PacvueColumn.VENDOR_CODE] = vendorCode;
    row[PacvueColumn.ITEM_NAME] = title;
    row[PacvueColumn.ITEM_HIGHLIGHTS] = highlight;
    rows.push(row);
  }

  return { rows, skipped };
};

/** Split into files of at most `size` data rows. Returns [] for no rows. */
export const chunkRows = <T>(rows: T[], size: number): T[][] => {
  if (size < 1) {
    throw new Error('chunkRows: size must be at least 1');
  }
  const chunks: T[][] = [];
  for (let i = 0; i < rows.length; i += size) {
    chunks.push(rows.slice(i, i + size));
  }
  return chunks;
};

/**
 * Render one file: optional instruction row, header row, then data rows —
 * matching the shape Pacvue's own template download has.
 */
export const buildPacvueCsv = (dataRows: string[][], includeInstructionsRow: boolean): string => {
  const lines: string[] = [];
  if (includeInstructionsRow) {
    // Row 1 is a single populated cell in column A; the rest of the row is empty.
    lines.push(escapeCsvCell(PACVUE_INSTRUCTIONS_ROW));
  }
  lines.push(PACVUE_HEADERS.map(escapeCsvCell).join(','));
  for (const row of dataRows) {
    lines.push(row.map(escapeCsvCell).join(','));
  }
  return lines.join(ROW_SEPARATOR);
};

/** `pacvue_listing_update_20260921_2_of_9.csv` — sorts chronologically, then by part. */
export const buildPacvueFileName = (part: number, totalParts: number, now: Date): string => {
  const stamp = [
    now.getFullYear(),
    String(now.getMonth() + 1).padStart(2, '0'),
    String(now.getDate()).padStart(2, '0')
  ].join('');
  return `pacvue_listing_update_${stamp}_${part}_of_${totalParts}.csv`;
};

/**
 * Everything the export UI needs to describe the download — file names, row
 * counts and the skip breakdown — WITHOUT rendering any CSV text.
 *
 * Split out because the modal recomputes this on every keystroke in the Account
 * ID field. Rendering the CSVs there would re-escape every cell of every row on
 * each character typed, which is tens of thousands of regex tests per keypress
 * on a full catalog. Callers render a file's text only when it is downloaded.
 */
export const planPacvueExport = (
  products: AuditedProduct[],
  store: TitleRewriteStore,
  config: PacvueExportConfig,
  now: Date = new Date()
): PacvueExportPlan => {
  const errors = validatePacvueConfig(config);
  if (errors.length > 0) {
    return { files: [], skipped: [], exportedCount: 0, errors };
  }

  const { rows, skipped } = selectPacvueRows(products, store, config);
  const chunks = chunkRows(rows, config.maxRowsPerFile ?? PACVUE_MAX_ROWS_PER_FILE);

  const files: PacvuePlannedFile[] = chunks.map((chunk, index) => ({
    fileName: buildPacvueFileName(index + 1, chunks.length, now),
    rowCount: chunk.length,
    rows: chunk
  }));

  return { files, skipped, exportedCount: rows.length, errors: [] };
};

/** Render one planned file's CSV text. Called at download time, not on render. */
export const renderPlannedFile = (file: PacvuePlannedFile, config: PacvueExportConfig): string =>
  buildPacvueCsv(file.rows, config.includeInstructionsRow ?? true);

/**
 * Full export: plan it, then render every file.
 *
 * Never throws for data reasons — a caller gets `errors` (config is unusable,
 * no files produced) or `files` plus `skipped` (what went, and what didn't).
 */
export const buildPacvueExport = (
  products: AuditedProduct[],
  store: TitleRewriteStore,
  config: PacvueExportConfig,
  now: Date = new Date()
): PacvueExportResult => {
  const plan = planPacvueExport(products, store, config, now);

  const files: PacvueExportFile[] = plan.files.map(file => ({
    fileName: file.fileName,
    csv: renderPlannedFile(file, config),
    rowCount: file.rowCount
  }));

  return {
    files,
    skipped: plan.skipped,
    exportedCount: plan.exportedCount,
    errors: plan.errors
  };
};
